import { Injectable } from "@angular/core";
import { BlockInterface } from "../interface/block.interface";
import * as _ from 'lodash';
import { DateDifferencePipe } from "app/shared/pipes/date-difference/date-difference.pipe";
import { BlockService } from "app/shared/services/block-service/block.service";
import { TranslateService } from "@ngx-translate/core";
import { DecimalPipe } from "@angular/common";
import { DateTimeFormatPreferencePipe } from "app/shared/pipes/date-time-format-preference/date-time-format-preference.pipe";
import { CustomNumberFormatPipe } from "@wind-log_library/fe-component-library";
import { DurationPipe } from "app/shared/pipes/duration/duration.pipe";

@Injectable({
  providedIn: 'root'
})

export class LogbookExportExcelHelper {
  constructor(
    private dateDifferencePipe: DateDifferencePipe,
    private translate: TranslateService,
    private customNumberFormat: CustomNumberFormatPipe,
    private decimalPipe: DecimalPipe,
    private dateTimeFormatPipe: DateTimeFormatPreferencePipe,
    private blockService: BlockService, 
    private durationPipe: DurationPipe) {}

  public getLogbookTableToExport(data: any, columnsToDisplay: string[]): any {
    const table = _.cloneDeep(data);
    const tableData = [];
    table.forEach((block: BlockInterface, i: number) => {
      tableData.push(block);
      if (block.open && block.events && block.events.length > 0) {
        tableData.push({});
        tableData.push(...block.events);
      }
    })
    const tableToExport = [];
  
    const columnMap = this.logbookTableColumnNamesMap();
    const exportedFields = columnMap.filter(element => columnsToDisplay.includes(element.key)).map(c => c.value);
    let blockCount = 1;
    tableData.forEach((d: BlockInterface) => {
      let row = {};
      if (d.hasOwnProperty('mutTS')) {
        row = this.mapRowData(d, blockCount, 'block', exportedFields);
        blockCount += 1;
      } else {
        row = this.mapRowData(d, blockCount, 'event', exportedFields);
      }
      tableToExport.push(row);
    })
  
    return tableToExport;
  }

  private logbookTableColumnNamesMap(): {key: string, value: string, columnWidth: number}[] {
    return [
      {key: 'startDate', value: this.translate.instant('LOGBOOK.TURBINE.START_DATE'), columnWidth: 80},
      {key: 'startTime', value: this.translate.instant('LOGBOOK.TURBINE.START_TIME'), columnWidth: 80},
      {key: 'endTime', value: this.translate.instant('LOGBOOK.TURBINE.END_TIME'), columnWidth: 80},
      {key: 'duration', value: this.translate.instant('LOGBOOK.TURBINE.DURATION'), columnWidth: 90},
      {key: 'warning', value: 'Acknowledged', columnWidth: 80},
      {key: 'tclass', value: this.translate.instant('LOGBOOK.TURBINE.TCLASS'), columnWidth: 270},
      {key: 'log', value: this.translate.instant('LOGBOOK.TURBINE.LOG'), columnWidth: 200},
      {key: 'component', value: this.translate.instant('LOGBOOK.TURBINE.COMP'), columnWidth: 100},
      {key: 'totalProductionLoss', value: this.translate.instant('LOGBOOK.TURBINE.PROD_LOSS'), columnWidth: 100},
      {key: 'totalRevenueLoss', value: this.translate.instant('LOGBOOK.TURBINE.REV_LOSS'), columnWidth: 100},
      {key: 'actualProduction', value: this.translate.instant('LOGBOOK.TURBINE.ACT_LOSS'), columnWidth: 100},
      {key: 'theoreticalProduction', value: this.translate.instant('LOGBOOK.TURBINE.THEO_PROD'), columnWidth: 100},
      {key: 'wil', value: this.translate.instant('LOGBOOK.TURBINE.WIL'), columnWidth: 100},
      {key: 'wol', value: this.translate.instant('LOGBOOK.TURBINE.WOL'), columnWidth: 100},
    ];
  }

  private mapRowData(row: any, blockCount: number, rowType: 'block' | 'event', exportedFields: string[]): {} {
    let formattedRow = {}
    if (rowType === 'block') {
      formattedRow = {
        ['#']: blockCount,
        [this.translate.instant('LOGBOOK.TURBINE.START_DATE')]: this.dateTimeFormatPipe.transform(row.startTSExport as any, 'dmy'),
        [this.translate.instant('LOGBOOK.TURBINE.START_TIME')]: this.dateTimeFormatPipe.transform(row.startTSExport as any, 'ts'),
        [this.translate.instant('LOGBOOK.TURBINE.END_TIME')]: this.dateTimeFormatPipe.transform(row.endTSExport as any, 'ts'),
        [this.translate.instant('LOGBOOK.TURBINE.DURATION')]: this.dateDifferencePipe.transform(row.endTSExport, [row.startTSExport]).replace(/<\/?b>|&nbsp;|<\/?small>|\s+/gi, ''),
        ['Acknowledged']: row.ack ? this.translate.instant('COMMON.YES') : this.translate.instant('COMMON.NO'),
        [this.translate.instant('LOGBOOK.TURBINE.TCLASS')]: this.blockService.getClassName(row),
        [this.translate.instant('LOGBOOK.TURBINE.LOG')]: row.log || '',
        [this.translate.instant('LOGBOOK.TURBINE.COMP')]: row.component || '',
        [this.translate.instant('LOGBOOK.TURBINE.PROD_LOSS')]: (row.totalProductionLoss && row.tcu !== 'T0' && row.tcu !== 'T5' && row.tcu !== 'T6' ? (this.customNumberFormat.transform(this.decimalPipe.transform(row.totalProductionLoss, '1.2-2')) + ' kWh') : ''),
        [this.translate.instant('LOGBOOK.TURBINE.REV_LOSS')]: (row.totalRevenueLoss && row.tcu !== 'T0' && row.tcu !== 'T5' && row.tcu !== 'T6' ? (this.customNumberFormat.transform(this.decimalPipe.transform(row.totalRevenueLoss, '1.2-2') + ' €')) : ''),
        [this.translate.instant('LOGBOOK.TURBINE.ACT_LOSS')]: (row.actualProductionExport ? (this.customNumberFormat.transform(this.decimalPipe.transform(row.actualProductionExport, '1.2-2') + ' kWh')) : ''),
        // looks like we don`t have theoreticalProduction in exported table at all
        [this.translate.instant('LOGBOOK.TURBINE.THEO_PROD')]: (row.theoreticalProduction ? (this.customNumberFormat.transform(this.decimalPipe.transform(row.theoreticalProduction, '1.2-2') + ' kWh')) : ''),
        [this.translate.instant('LOGBOOK.TURBINE.WIL')]: (row.wilExport ? (this.durationPipe.transform(row.wilExport, true)) : ''),
        [this.translate.instant('LOGBOOK.TURBINE.WOL')]: (row.wolExport ? (this.durationPipe.transform(row.wolExport, true)) : ''),
      }
    } else {
      formattedRow = {
        ['#']: '',
        [this.translate.instant('LOGBOOK.TURBINE.START_DATE')]: Object.keys(row).length > 0 ? this.dateTimeFormatPipe.transform(row.createTS as any, 'dmy') : '',
        [this.translate.instant('LOGBOOK.TURBINE.START_TIME')]: Object.keys(row).length > 0 ? this.dateTimeFormatPipe.transform(row.createTS as any, 'ts') : '',
        [this.translate.instant('LOGBOOK.TURBINE.END_TIME')]: '',
        [this.translate.instant('LOGBOOK.TURBINE.DURATION')]: '',
        ['Acknowledged']: '',
        [this.translate.instant('LOGBOOK.TURBINE.TCLASS')]: Object.keys(row).length > 0 ? row.code + ' - ' + row.description : '',
        [this.translate.instant('LOGBOOK.TURBINE.LOG')]: '',
        [this.translate.instant('LOGBOOK.TURBINE.COMP')]: '',
        [this.translate.instant('LOGBOOK.TURBINE.PROD_LOSS')]: '',
        [this.translate.instant('LOGBOOK.TURBINE.REV_LOSS')]: '',
        [this.translate.instant('LOGBOOK.TURBINE.ACT_LOSS')]: '',
        [this.translate.instant('LOGBOOK.TURBINE.THEO_PROD')]: '',
        [this.translate.instant('LOGBOOK.TURBINE.WIL')]: '',
        [this.translate.instant('LOGBOOK.TURBINE.WOL')]: '',
      }
    }
    Object.keys(formattedRow).forEach(k => {
      if (k !== '#' && !exportedFields.includes(k)) {
        delete formattedRow[k];
      }
    })
    return formattedRow;
  }

  public getExcelFileColumnWidth(tableData): {wpx: number}[] {
    const columnWidth = this.logbookTableColumnNamesMap()
      .filter(c => Object.keys(tableData[0]).includes(c.value))
      .map(c => {return {wpx: c.columnWidth}});
    columnWidth.unshift({wpx: 35})
    return columnWidth;
  }
}

